// frontend/src/components/responsive-fixes/MobileNavDrawer.tsx
//
// Off-canvas drawer that overlays the page on touch / narrow viewports
// with the same NAV_GROUPS the desktop Sidebar renders. Fixes audit
// PR #51 items C1 (sidebar drawer absent <900px) and C2 (sidebar
// label-on-hover unreachable on touch).
//
// NON-INVASIVE: this component does not edit Sidebar.tsx, it does not
// edit majestic.css / layout.css / tokens.css, and it adds zero new
// branding tokens — every color comes from BRAND. The drawer uses the
// Sidebar component's underlying NAV_GROUPS shape (mirrored here so we
// do not import Sidebar.tsx and risk a circular render).

import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BRAND } from "../../branding";

interface NavItem {
  path: string;
  icon: string;
  label: string;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

// Mirror of Sidebar.tsx's NAV_GROUPS (kept manually in sync; if a
// desktop nav link is added there, mirror it here in the same PR).
const NAV_GROUPS: NavGroup[] = [
  {
    label: "Core",
    items: [
      { path: "/", icon: "space_dashboard", label: "Command Center" },
      { path: "/whatsapp", icon: "chat", label: "WhatsApp" },
      { path: "/crm", icon: "contacts", label: "CRM" },
    ],
  },
  {
    label: "Commerce",
    items: [
      { path: "/inventory", icon: "inventory_2", label: "Inventory" },
      { path: "/shopify", icon: "storefront", label: "Shopify" },
      { path: "/pricing", icon: "sell", label: "Pricing" },
      { path: "/marketplace", icon: "hub", label: "Marketplace" },
      { path: "/accounting", icon: "receipt_long", label: "AfaqBooks" },
      { path: "/email", icon: "mail", label: "Email Intel" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { path: "/seo", icon: "query_stats", label: "SEO Intel" },
      { path: "/voice-sales", icon: "graphic_eq", label: "Voice Sales" },
      { path: "/market-intel", icon: "insights", label: "Market Intel" },
      { path: "/leads", icon: "person_search", label: "Leads" },
      { path: "/ai", icon: "psychology", label: "AI Center" },
      { path: "/content", icon: "auto_awesome_motion", label: "Content AI" },
      { path: "/agents", icon: "smart_toy", label: "Agent Hub" },
      { path: "/automation", icon: "smart_toy", label: "Automation" },
      { path: "/veridian", icon: "auto_awesome", label: "Veridian Ginie" },
      { path: "/omnivoice", icon: "surround_sound", label: "OmniVoice" },
      { path: "/crawler", icon: "radar", label: "Crawler" },
      { path: "/genie", icon: "monitor_heart", label: "Genie Pulse" },
    ],
  },
  {
    label: "People",
    items: [
      { path: "/attendance", icon: "fingerprint", label: "Attendance" },
      { path: "/hr", icon: "badge", label: "HR" },
      { path: "/commission", icon: "payments", label: "Commission" },
      { path: "/my-dashboard", icon: "person", label: "My Dashboard" },
    ],
  },
  {
    label: "System",
    items: [
      { path: "/admin", icon: "shield", label: "Admin" },
      { path: "/integrations", icon: "cable", label: "Integrations" },
      { path: "/gpu", icon: "memory", label: "GPU Intelligence" },
      { path: "/phone-bridge", icon: "phonelink", label: "Phone Bridge" },
      { path: "/help", icon: "help", label: "Help Centre" },
      { path: "/profile", icon: "manage_accounts", label: "Profile" },
      { path: "/harmony", icon: "sensors", label: "HARMONY" },
    ],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

const MobileNavDrawer: React.FC<Props> = ({ open, onClose }) => {
  const { pathname } = useLocation();

  // Close on route change. (We intentionally only depend on pathname; if
  // onClose changes between renders we still want a single close-on-navigate
  // semantic, not a re-fire.)
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line
  }, [pathname]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while open (avoids double-scroll trap on touch).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const isActive = (path: string) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.25s ease",
          zIndex: 998,
        }}
      />
      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        aria-hidden={!open}
        style={{
          position: "fixed",
          top: 0,
          bottom: 0,
          left: 0,
          width: "min(280px, 86vw)",
          background: "rgba(9,19,39,0.97)",
          backdropFilter: "blur(60px)",
          WebkitBackdropFilter: "blur(60px)",
          borderRight: "0.5px solid rgba(189,178,255,0.12)",
          boxShadow: "4px 0 30px rgba(0,0,0,0.5)",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          fontFamily: BRAND.fontFamily,
          color: "#dde5ff",
        }}
      >
        <div style={{ padding: "18px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800, fontSize: 15, color: BRAND.colors.primary,
              letterSpacing: "-0.02em",
            }}>
              {BRAND.name}
            </div>
            <div style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 9, fontWeight: 600, letterSpacing: "0.2em",
              textTransform: "uppercase", color: "rgba(221,229,255,0.4)",
              marginTop: 2,
            }}>
              {BRAND.tagline}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close navigation"
            style={{
              background: "rgba(189,178,255,0.08)",
              border: "0.5px solid rgba(189,178,255,0.18)",
              color: BRAND.colors.primary,
              borderRadius: 10,
              width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>

        <nav style={{ flex: 1, padding: "4px 8px 24px" }}>
          {NAV_GROUPS.map((group) => (
            <div key={group.label} style={{ marginBottom: 6 }}>
              <div style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 10, fontWeight: 700, letterSpacing: "0.15em",
                textTransform: "uppercase", color: "rgba(189,178,255,0.4)",
                padding: "12px 12px 4px",
              }}>
                {group.label}
              </div>
              {group.items.map(({ path, icon, label }) => {
                const active = isActive(path);
                return (
                  <a
                    key={path}
                    href={path}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px",
                      minHeight: 44, // touch target per audit M8
                      borderRadius: 10,
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      color: active ? BRAND.colors.primary : "rgba(221,229,255,0.7)",
                      background: active ? "rgba(189,178,255,0.10)" : "transparent",
                      textDecoration: "none",
                      border: active ? "1px solid rgba(189,178,255,0.3)" : "1px solid transparent",
                      transition: "background 0.15s",
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
                    <span>{label}</span>
                  </a>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default MobileNavDrawer;
